import { sendRequest } from '@infrastructure/api/api';
import { EntityScope, UserAccount, UserAccountsResponse } from '../types/entity';
import ApiResponse from '@core/types/apiResponse';

export interface UserAccessPermission {
  action_code: string;
  department_id: number;
}

export interface LeadershipNavigation {
  show_section: boolean;
  pastor: boolean;
  finance_statistics: boolean;
  district_overseer: boolean;
}

export interface UserAccessWithLeadership {
  permissions: UserAccessPermission[];
  leadership: LeadershipNavigation;
}

export interface FetchUserAccessOptions {
  departmentId?: number;
  includeLeadership?: boolean;
}

export interface FetchUserAccountsParams {
  page?: number;
  perPage?: number;
  scope?: EntityScope;
  /** Only applied together with scope. */
  scopeRefId?: number;
}

const userAccountsInFlight = new Map<string, Promise<ApiResponse<UserAccountsResponse>>>();
const userAccessInFlight = new Map<string, Promise<ApiResponse<UserAccessPermission[] | UserAccessWithLeadership>>>();

function buildUserAccessKey(
  scope: string,
  scopeRefId: number,
  actions: string[],
  options?: FetchUserAccessOptions
) {
  return JSON.stringify({
    scope,
    scopeRefId,
    departmentId: options?.departmentId ?? null,
    includeLeadership: options?.includeLeadership ?? false,
    actions: [...actions].sort(),
  });
}

function buildUserAccountsQuery(params: FetchUserAccountsParams) {
  const query = new URLSearchParams();
  if (params.page != null) query.set('page', String(params.page));
  if (params.perPage != null) query.set('perPage', String(params.perPage));
  if (params.scope) query.set('scope', params.scope);
  if (params.scope && params.scopeRefId != null) query.set('scopeRefId', String(params.scopeRefId));
  return query.toString();
}

function isWrappedUserAccess(
  data: UserAccessPermission[] | UserAccessWithLeadership
): data is UserAccessWithLeadership {
  return !Array.isArray(data) && Array.isArray((data as UserAccessWithLeadership).permissions);
}

/**
 * Fetches one page of entity accounts (nation, district, church) for the authenticated user.
 * Identical requests that are still in flight share one network call.
 * @param params - page, perPage, scope, scopeRefId (all optional; API default page size is 20)
 * @returns Promise<ApiResponse<UserAccountsResponse>> including scope_totals
 */
export const fetchUserAccounts = async (
  params: FetchUserAccountsParams = {}
): Promise<ApiResponse<UserAccountsResponse>> => {
  const query = buildUserAccountsQuery(params);

  if (!userAccountsInFlight.has(query)) {
    userAccountsInFlight.set(
      query,
      sendRequest<any, UserAccountsResponse>('GET', `/user/account/all${query ? `?${query}` : ''}`).finally(() => {
        userAccountsInFlight.delete(query);
      })
    );
  }

  return userAccountsInFlight.get(query)!;
};

/**
 * Looks up the user's account for a single entity.
 * @returns The account row, or null when the user has no account there.
 * @throws When the request itself fails.
 */
export const fetchUserAccount = async (scope: EntityScope, scopeRefId: number): Promise<UserAccount | null> => {
  const response = await fetchUserAccounts({ perPage: 1, scope, scopeRefId });
  if (!response.isSuccess()) {
    throw new Error('Failed to look up workspace access');
  }
  const rows = response.getData?.()?.data ?? response.data?.data ?? [];
  return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
};

/**
 * Checks which action codes the authenticated user has access to
 * for a specific scope and entity, optionally filtered by department.
 *
 * When `includeLeadership` is true, the response is wrapped with a `leadership` block.
 */
export const fetchUserAccess = async (
  scope: string,
  scopeRefId: number,
  actions: string[],
  options?: FetchUserAccessOptions
): Promise<ApiResponse<UserAccessPermission[] | UserAccessWithLeadership>> => {
  const requestKey = buildUserAccessKey(scope, scopeRefId, actions, options);

  if (!userAccessInFlight.has(requestKey)) {
    const body: Record<string, unknown> = {
      scope,
      scope_ref_id: scopeRefId,
      actions,
    };
    if (options?.departmentId != null && options.departmentId > 0) {
      body.department_id = options.departmentId;
    }
    if (options?.includeLeadership) {
      body.include_leadership = true;
    }

    userAccessInFlight.set(
      requestKey,
      sendRequest<any, UserAccessPermission[] | UserAccessWithLeadership>('POST', '/user/access', body).finally(() => {
        userAccessInFlight.delete(requestKey);
      })
    );
  }

  return userAccessInFlight.get(requestKey)!;
};

export { isWrappedUserAccess };
